import { useEffect, useState } from "react";
import { Globe2, MapPin } from "lucide-react";
import GradientBg from "@/components/common/GradientBg";
import HeadMeta from "@/components/common/HeadMeta";
import Reveal from "@/components/marketing/Reveal";
import WorldMap from "@/components/marketing/WorldMap";
import api from "@/lib/api";
import { BRAND } from "@/branding.config";

export default function Locations() {
  const [locs, setLocs] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/public/locations").then((r) => setLocs(r.data.locations)); }, []);

  const grouped = locs.reduce((acc, l) => {
    (acc[l.region] = acc[l.region] || []).push(l);
    return acc;
  }, {});

  return (
    <>
      <HeadMeta title="Global locations" description={`${BRAND.app_name} operates in 12 tier-3 regions across three continents.`} />
      <div className="relative">
        <GradientBg variant="soft" />

        <section className="relative mx-auto max-w-7xl px-6 pt-28 pb-12">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Global infrastructure</div>
            <h1 className="mt-4 text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95] max-w-3xl">
              One network. <span className="brand-gradient-text">Twelve edges.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              {BRAND.app_name} operates in tier-3+ facilities across three continents, connected
              by a dedicated 400 Gbps backbone. Deploy where your users, brokers or workloads live.
            </p>
          </Reveal>
        </section>

        <section className="mx-auto max-w-7xl px-6">
          <Reveal><WorldMap highlight={active} /></Reveal>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-20 space-y-12">
          {Object.entries(grouped).map(([region, items]) => (
            <div key={region}>
              <Reveal>
                <div className="flex items-center gap-3 mb-6">
                  <Globe2 size={16} className="text-primary" />
                  <h2 className="text-xl font-bold tracking-tight">{region}</h2>
                  <div className="flex-1 h-px bg-white/[0.06]" />
                  <span className="text-xs text-muted-foreground font-mono">{items.length} regions</span>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {items.map((l, i) => (
                  <Reveal key={l.code} delay={i * 0.05}>
                    <div
                      data-testid={`location-${l.code}`}
                      onMouseEnter={() => setActive(l.code)}
                      onMouseLeave={() => setActive(null)}
                      className="glass p-5 transition-transform duration-300 hover:-translate-y-0.5"
                    >
                      <div className="flex items-center justify-between">
                        <MapPin size={14} className="text-primary" />
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{l.code}</span>
                      </div>
                      <div className="mt-4 text-lg font-bold">{l.city}</div>
                      <div className="text-xs text-muted-foreground">{l.country}</div>
                      <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Latency</span>
                        <span className="font-mono font-semibold text-emerald-500">{l.latency_ms}ms</span>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </section>

        <section className="mx-auto max-w-4xl px-6 pb-32 text-center">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Coming soon</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-black tracking-tight">Dubai · São Paulo · Mumbai · Johannesburg</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">Four new regions land in 2026. Existing customers get priority access and free migration credits.</p>
          </Reveal>
        </section>
      </div>
    </>
  );
}
